import React, { useState } from 'react';
import { Home, Building2, Truck, Snowflake, Building, ShieldCheck, ArrowRight } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Home,
  Building2,
  Truck,
  Snowflake,
  Building,
  ShieldCheck,
};

const ServicesSection = ({ onGetEstimate }) => {
  const [activeTab, setActiveTab] = useState('all');

  const filteredServices = activeTab === 'all' 
    ? services 
    : services.filter(s => s.category === activeTab);

  const tabs = [
    { id: 'all', label: 'All Services' },
    { id: 'junk', label: 'Junk Removal' },
    { id: 'snow', label: 'Snow Clearing' },
  ];

  return (
    <section id="services" className="py-20 bg-[#111111]">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-[#7BA428]/10 text-[#7BA428] px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-[#7BA428]/20">
            Our Services
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            How Can We Help You?
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            From clearing clutter to keeping your property snow-free, we've got you covered year-round.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex justify-center gap-2 mb-12">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-full font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-[#7BA428] text-black'
                  : 'bg-[#1A1A1A] text-gray-300 hover:bg-[#2A2A2A] border border-[#2A2A2A]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className="group bg-[#141414] border border-[#2A2A2A] rounded-3xl p-8 hover:border-[#7BA428]/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Icon */}
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${
                  service.category === 'snow' 
                    ? 'bg-white/10 text-white' 
                    : 'bg-[#7BA428] text-black'
                }`}>
                  {IconComponent && <IconComponent className="w-7 h-7" />}
                </div>

                {/* Category Badge */}
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${
                  service.category === 'snow'
                    ? 'bg-white/10 text-white'
                    : 'bg-[#7BA428]/20 text-[#7BA428]'
                }`}>
                  {service.category === 'snow' ? 'Snow Removal' : 'Junk Removal'}
                </span>

                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#7BA428] transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#7BA428]" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Learn More Link */}
                <button 
                  onClick={onGetEstimate}
                  className="flex items-center gap-2 text-[#7BA428] font-semibold group-hover:gap-3 transition-all"
                >
                  Get Quote
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
